import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type Ref,
  type RefObject,
} from "react";
import { useNavigate } from "react-router-dom";

import type { EditionSummary } from "@/types";

interface Props {
  editions: EditionSummary[];
  selected: string;
}

interface DraftDate {
  year: number;
  month: number;
  day: number;
}

const MONTH_SHORT = [
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic",
] as const;

const EMPTY_SLOT = "—";

function parseDate(iso: string): DraftDate {
  const [y, m, d] = iso.split("-").map(Number);
  return { year: y ?? 2026, month: m ?? 1, day: d ?? 1 };
}

function toIso({ year, month, day }: DraftDate): string {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function monthLabel(month: number): string {
  return MONTH_SHORT[month - 1] ?? String(month);
}

function formatSelectedDate(iso: string): string {
  const { year, month, day } = parseDate(iso);
  return `${day} ${monthLabel(month)} ${year}`;
}

function availableYears(editions: EditionSummary[]): number[] {
  const years = new Set<number>();
  for (const edition of editions) {
    years.add(parseDate(edition.fecha).year);
  }
  return [...years].sort((a, b) => a - b);
}

function availableMonths(editions: EditionSummary[], year: number): number[] {
  const months = new Set<number>();
  for (const edition of editions) {
    const parsed = parseDate(edition.fecha);
    if (parsed.year === year) months.add(parsed.month);
  }
  return [...months].sort((a, b) => a - b);
}

function availableDays(
  editions: EditionSummary[],
  year: number,
  month: number,
): number[] {
  const days = new Set<number>();
  for (const edition of editions) {
    const parsed = parseDate(edition.fecha);
    if (parsed.year === year && parsed.month === month) days.add(parsed.day);
  }
  return [...days].sort((a, b) => a - b);
}

function reconcileDraft(
  editions: EditionSummary[],
  draft: DraftDate,
): DraftDate {
  const years = availableYears(editions);
  const year = years.includes(draft.year) ? draft.year : (years[0] ?? draft.year);

  const months = availableMonths(editions, year);
  const month = months.includes(draft.month) ? draft.month : (months[0] ?? draft.month);

  const days = availableDays(editions, year, month);
  const day = days.includes(draft.day) ? draft.day : (days[0] ?? draft.day);

  return { year, month, day };
}

function closestIndex(container: HTMLElement): number {
  const items = Array.from(container.querySelectorAll<HTMLElement>("[data-wheel-index]"));
  if (items.length === 0) return 0;

  const center = container.scrollTop + container.clientHeight / 2;
  let bestIdx = 0;
  let bestDist = Number.POSITIVE_INFINITY;

  for (const item of items) {
    const itemCenter = item.offsetTop + item.offsetHeight / 2;
    const dist = Math.abs(itemCenter - center);
    const idx = Number(item.dataset.wheelIndex);
    if (dist < bestDist) {
      bestDist = dist;
      bestIdx = idx;
    }
  }

  return bestIdx;
}

function scrollWheelToIndex(
  wheelRef: RefObject<HTMLUListElement | null>,
  index: number,
  behavior: ScrollBehavior = "auto",
) {
  const container = wheelRef.current;
  if (!container || index < 0) return;
  const item = container.querySelector<HTMLElement>(`[data-wheel-index="${index}"]`);
  item?.scrollIntoView({ block: "center", behavior });
}

function WheelColumn({
  values,
  selectedIndex,
  onSelectIndex,
  onStep,
  onScrollEnd,
  wheelRef,
  stepUpLabel,
  stepDownLabel,
  align,
}: {
  values: string[];
  selectedIndex: number;
  onSelectIndex: (index: number) => void;
  onStep: (delta: -1 | 1) => void;
  onScrollEnd: () => void;
  wheelRef: Ref<HTMLUListElement>;
  stepUpLabel: string;
  stepDownLabel: string;
  align: "start" | "center" | "end";
}) {
  if (values.length === 0) {
    return (
      <div className={`pulse-today-card__col pulse-today-card__col--${align} pulse-today-card__col--empty`} />
    );
  }

  const canStepUp = selectedIndex > 0;
  const canStepDown = selectedIndex < values.length - 1;

  return (
    <div className={`pulse-today-card__col pulse-today-card__col--${align}`}>
      <button
        type="button"
        className="pulse-today-wheel__step pulse-today-wheel__step--up"
        aria-label={stepUpLabel}
        disabled={!canStepUp}
        onClick={() => onStep(-1)}
      />
      <ul
        ref={wheelRef}
        className="pulse-today-wheel"
        role="listbox"
        onScroll={onScrollEnd}
      >
        <li className="pulse-today-wheel__spacer" aria-hidden>
          {EMPTY_SLOT}
        </li>
        {values.map((label, index) => (
          <li
            key={`${label}-${index}`}
            role="option"
            aria-selected={index === selectedIndex}
            data-wheel-index={index}
          >
            <button
              type="button"
              onClick={() => onSelectIndex(index)}
              className={`pulse-today-wheel__item ${
                index === selectedIndex ? "is-selected" : ""
              }`}
            >
              {label}
            </button>
          </li>
        ))}
        <li className="pulse-today-wheel__spacer" aria-hidden>
          {EMPTY_SLOT}
        </li>
      </ul>
      <button
        type="button"
        className="pulse-today-wheel__step pulse-today-wheel__step--down"
        aria-label={stepDownLabel}
        disabled={!canStepDown}
        onClick={() => onStep(1)}
      />
    </div>
  );
}

export default function PulseDatePicker({ editions, selected }: Props) {
  const navigate = useNavigate();
  const popoverId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const dayWheelRef = useRef<HTMLUListElement>(null);
  const monthWheelRef = useRef<HTMLUListElement>(null);
  const yearWheelRef = useRef<HTMLUListElement>(null);
  const scrollEndTimerRef = useRef<number | null>(null);
  const isProgrammaticScrollRef = useRef(false);

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DraftDate>(() => reconcileDraft(editions, parseDate(selected)));

  const latestEdition = editions[0]?.fecha;
  const isViewingLatest = !latestEdition || selected === latestEdition;

  const years = useMemo(() => availableYears(editions), [editions]);
  const months = useMemo(
    () => availableMonths(editions, draft.year),
    [editions, draft.year],
  );
  const days = useMemo(
    () => availableDays(editions, draft.year, draft.month),
    [editions, draft.year, draft.month],
  );

  const yearValues = years.map(String);
  const monthValues = months.map(monthLabel);
  const dayValues = days.map((day) => String(day).padStart(2, "0"));

  const yearIndex = Math.max(0, years.indexOf(draft.year));
  const monthIndex = Math.max(0, months.indexOf(draft.month));
  const dayIndex = Math.max(0, days.indexOf(draft.day));

  useEffect(() => {
    setDraft(reconcileDraft(editions, parseDate(selected)));
  }, [editions, selected]);

  const navigateToDate = useCallback(
    (iso: string) => {
      if (iso === latestEdition) {
        navigate("/");
        return;
      }
      navigate(`/dia/${iso}`);
    },
    [latestEdition, navigate],
  );

  const updateDraft = useCallback(
    (next: DraftDate) => {
      setDraft(reconcileDraft(editions, next));
    },
    [editions],
  );

  const closePicker = useCallback(() => {
    setDraft(reconcileDraft(editions, parseDate(selected)));
    setOpen(false);
  }, [editions, selected]);

  const confirmSelection = useCallback(() => {
    const reconciled = reconcileDraft(editions, draft);
    setDraft(reconciled);
    navigateToDate(toIso(reconciled));
    setOpen(false);
  }, [draft, editions, navigateToDate]);

  const syncWheels = useCallback(
    (behavior: ScrollBehavior = "auto") => {
      isProgrammaticScrollRef.current = true;
      scrollWheelToIndex(yearWheelRef, yearIndex, behavior);
      scrollWheelToIndex(monthWheelRef, monthIndex, behavior);
      scrollWheelToIndex(dayWheelRef, dayIndex, behavior);
      window.requestAnimationFrame(() => {
        isProgrammaticScrollRef.current = false;
      });
    },
    [dayIndex, monthIndex, yearIndex],
  );

  useEffect(() => {
    if (!open) return;
    const frame = window.requestAnimationFrame(() => syncWheels("auto"));
    return () => window.cancelAnimationFrame(frame);
  }, [open, draft.year, draft.month, draft.day, syncWheels]);

  useEffect(() => {
    if (!open) return;

    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      closePicker();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closePicker();
    };

    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [closePicker, open]);

  useEffect(() => {
    return () => {
      if (scrollEndTimerRef.current !== null) {
        window.clearTimeout(scrollEndTimerRef.current);
      }
    };
  }, []);

  const scheduleScrollApply = (column: "day" | "month" | "year") => {
    if (isProgrammaticScrollRef.current) return;

    if (scrollEndTimerRef.current !== null) {
      window.clearTimeout(scrollEndTimerRef.current);
    }

    scrollEndTimerRef.current = window.setTimeout(() => {
      if (isProgrammaticScrollRef.current) return;

      const columnRef =
        column === "day"
          ? dayWheelRef
          : column === "month"
            ? monthWheelRef
            : yearWheelRef;

      const container = columnRef.current;
      if (!container) return;

      const index = closestIndex(container);

      setDraft((current) => {
        let next = current;

        if (column === "year") {
          const year = years[index];
          if (year === undefined) return current;
          next = { ...current, year };
        } else if (column === "month") {
          const month = months[index];
          if (month === undefined) return current;
          next = { ...current, month };
        } else {
          const day = days[index];
          if (day === undefined) return current;
          next = { ...current, day };
        }

        return reconcileDraft(editions, next);
      });
    }, 120);
  };

  const goToToday = () => {
    setOpen(false);
    // "Hoy" = última edición publicada, no la fecha calendario (puede no existir en BD).
    if (latestEdition) {
      navigateToDate(latestEdition);
      return;
    }
    navigate("/");
  };

  if (editions.length === 0) return null;

  return (
    <div ref={rootRef} className="pulse-header-actions__inner">
      <button
        type="button"
        id="pulse-today-toggle"
        className="pulse-today-toggle m-0"
        aria-label="Ir a la edición de hoy"
        onClick={goToToday}
      >
        {isViewingLatest ? "Hoy" : formatSelectedDate(selected)}
      </button>

      <button
        type="button"
        id="pulse-date-picker-toggle"
        className="pulse-header-action-btn m-0"
        aria-label="Seleccionar fecha"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={popoverId}
        onClick={() => setOpen((value) => !value)}
      >
        <svg
          className="pulse-header-action-btn__icon"
          width="56"
          height="56"
          viewBox="0 0 56 56"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <circle cx="28" cy="28" r="28" fill="white" />
          <path d="M27.8906 17.78V38.22" stroke="#003F28" strokeWidth="2" />
          <path d="M38.22 28.0278H17.78" stroke="#003F28" strokeWidth="2" />
        </svg>
      </button>

      {open && (
            <div
              id={popoverId}
              className="pulse-today-card"
              role="dialog"
              aria-label="Fecha de edicion"
            >
              <button
                type="button"
                id="pulse-today-close"
                className="pulse-today-card__close"
                aria-label="Cerrar selector de fecha"
                onClick={closePicker}
              >
                <svg
                  width="64"
                  height="64"
                  viewBox="0 0 64 64"
                  fill="none"
                  aria-hidden
                >
                  <circle cx="32" cy="32" r="32" fill="#FF8307" />
                  <path
                    d="M25.2 25.2L38.8 38.8M38.8 25.2L25.2 38.8"
                    stroke="#003F28"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>

              <p className="pulse-today-card__title">Fecha</p>

              <div className="pulse-today-card__picker" aria-label="Selector de fecha">
                <div className="pulse-today-card__grid">
                  <WheelColumn
                    values={dayValues}
                    selectedIndex={dayIndex}
                    align="start"
                    stepUpLabel="Dia anterior disponible"
                    stepDownLabel="Dia siguiente disponible"
                    onSelectIndex={(index) => {
                      const day = days[index];
                      if (day === undefined) return;
                      updateDraft({ ...draft, day });
                    }}
                    onStep={(delta) => {
                      const index = dayIndex + delta;
                      const day = days[index];
                      if (day === undefined) return;
                      updateDraft({ ...draft, day });
                    }}
                    onScrollEnd={() => scheduleScrollApply("day")}
                    wheelRef={dayWheelRef}
                  />
                  <WheelColumn
                    values={monthValues}
                    selectedIndex={monthIndex}
                    align="center"
                    stepUpLabel="Mes anterior disponible"
                    stepDownLabel="Mes siguiente disponible"
                    onSelectIndex={(index) => {
                      const month = months[index];
                      if (month === undefined) return;
                      updateDraft({ ...draft, month });
                    }}
                    onStep={(delta) => {
                      const index = monthIndex + delta;
                      const month = months[index];
                      if (month === undefined) return;
                      updateDraft({ ...draft, month });
                    }}
                    onScrollEnd={() => scheduleScrollApply("month")}
                    wheelRef={monthWheelRef}
                  />
                  <WheelColumn
                    values={yearValues}
                    selectedIndex={yearIndex}
                    align="center"
                    stepUpLabel="Ano anterior disponible"
                    stepDownLabel="Ano siguiente disponible"
                    onSelectIndex={(index) => {
                      const year = years[index];
                      if (year === undefined) return;
                      updateDraft({ ...draft, year });
                    }}
                    onStep={(delta) => {
                      const index = yearIndex + delta;
                      const year = years[index];
                      if (year === undefined) return;
                      updateDraft({ ...draft, year });
                    }}
                    onScrollEnd={() => scheduleScrollApply("year")}
                    wheelRef={yearWheelRef}
                  />
                </div>
              </div>

              <button
                type="button"
                className="pulse-today-card__confirm"
                onClick={confirmSelection}
              >
                Seleccionar
              </button>
            </div>
      )}
    </div>
  );
}
